import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
  Paper,
  IconButton,
  colors,
  Alert,
  Chip,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  EmojiEvents,
  PersonAdd,
  People,
  CalendarToday,
  TrendingUp,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  ThumbUp,
  Notifications,
  Star,
  Category,
  Group
} from '@mui/icons-material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import DashboardSkeleton from './components/dashboard_skeloton';
import { useGetDashboardSummaryQuery } from './api/settingsApi';
import { DashboardMetric, RecentActivity, TopNominee } from './types/dashboard';

ChartJS.register(...registerables);

// Mock user name (replace with real user info if available)
const USER_NAME = 'Admin';

// Helper function to get icon based on activity type
const getActivityIcon = (type: string) => {
  switch (type) {
    case 'nomination':
      return <PersonAdd />;
    case 'voting':
      return <ThumbUp />;
    case 'category':
      return <Category />;
    case 'award':
      return <EmojiEvents />;
    default:
      return <Notifications />;
  }
};

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
    case 'winner':
      return 'success';
    case 'active':
      return 'primary';
    case 'pending':
    case 'suggested':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to format time
const formatTime = (timeString: string) => {
  const date = new Date(timeString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours} hours ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays} days ago`;
  
  return date.toLocaleDateString();
};

const MetricCard = ({ title, value, icon, color, trend, subtitle }: any) => {
  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Avatar sx={{ 
            bgcolor: `${color}.100`, 
            color: `${color}.600`,
            width: 48,
            height: 48
          }}>
            {icon}
          </Avatar>
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" mt={1}>
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          {/* Trend indicator */}
          {trend && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {trend.direction === 'up' ? (
                <ArrowUpward fontSize="small" color="success" />
              ) : (
                <ArrowDownward fontSize="small" color="error" />
              )}
              <Typography variant="body2" color={trend.direction === 'up' ? 'success.main' : 'error.main'}>
                {trend.percent}%
              </Typography>
            </Stack>
          )}
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const RecentActivities = ({ activities }: any) => (
  <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%' }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Recent Activities
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {activities.length === 0 ? (
          <Typography color="text.secondary">No recent activities.</Typography>
        ) : (
          activities.map((activity: any, idx: number) => (
            <Stack direction="row" alignItems="flex-start" spacing={2} key={activity.id || idx}>
              <Avatar sx={{ 
                bgcolor: getStatusColor(activity.status) === 'success' ? 'success.100' : 
                         getStatusColor(activity.status) === 'primary' ? 'primary.100' : 'warning.100',
                color: getStatusColor(activity.status) === 'success' ? 'success.600' : 
                       getStatusColor(activity.status) === 'primary' ? 'primary.600' : 'warning.600',
                width: 32, 
                height: 32 
              }}>
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box flex={1}>
                <Typography variant="body2" fontWeight="medium">{activity.title}</Typography>
                <Typography variant="caption" color="text.secondary">{activity.subtitle}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatTime(activity.time)}
                </Typography>
              </Box>
              <Chip 
                label={activity.status} 
                size="small"
                color={getStatusColor(activity.status)}
                variant="outlined"
              />
            </Stack>
          ))
        )}
      </Stack>
    </CardContent>
  </Card>
);

const TopNominees = ({ nominees }: any) => (
  <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%' }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Top Nominees
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {nominees.length === 0 ? (
          <Typography color="text.secondary">No nominees available.</Typography>
        ) : (
          nominees.map((nominee: any, idx: number) => (
            <Box key={nominee.id || idx}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.100' }}>
                  <Star />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="medium">{nominee.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{nominee.category}</Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold">{nominee.votes} votes</Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={nominee.progress} 
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ))
        )}
      </Stack>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const theme = useTheme();
  const { data: dashboardData, isLoading, error } = useGetDashboardSummaryQuery();

  // Show loading skeleton
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </Box>
    );
  }

  // Extract data from API response
  const { metrics = [], charts = {}, recentActivities = [], topNominees = [] } = dashboardData?.data || {};

  // Transform chart data for Chart.js
  const nominationsChart = {
    labels: (charts as any).nominationsTrend?.labels || [],
    datasets: [{
      label: 'New Nominations',
      data: (charts as any).nominationsTrend?.data || [],
      backgroundColor: '#1976d2',
      borderColor: '#115293',
      tension: 0.1,
      fill: true,
    }],
  };

  const votingChart = {
    labels: (charts as any).votingActivity?.labels || [],
    datasets: [{
      label: 'Votes Cast',
      data: (charts as any).votingActivity?.data || [],
      backgroundColor: '#15B3D5',
      borderColor: '#0F9BB8',
      tension: 0.1,
      fill: true,
    }],
  };

  const awardCategoriesChart = {
    labels: (charts as any).awardCategories?.labels || [],
    datasets: [{
      data: (charts as any).awardCategories?.data?.map((d: string) => parseInt(d) || 0) || [],
      backgroundColor: [
        '#1976d2', '#15B3D5', '#ed6c02', '#9c27b0', '#d32f2f', '#7b1fa2',
        '#ff9800', '#4caf50', '#f44336', '#2196f3', '#9c27b0'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const nomineeStatusChart = {
    labels: (charts as any).nomineeStatus?.labels || [],
    datasets: [{
      data: (charts as any).nomineeStatus?.data || [],
      backgroundColor: [
        '#ff9800', '#4caf50', '#f44336', '#2196f3', '#9c27b0'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Map metrics to include icons
  const metricsWithIcons = metrics.map((metric: DashboardMetric, index: number) => ({
    ...metric,
    icon: metric.title === 'Active Awards' ? <EmojiEvents fontSize="medium" /> :
          metric.title === 'Total Nominees' ? <PersonAdd fontSize="medium" /> :
          metric.title === 'Total Votes Cast' ? <ThumbUp fontSize="medium" /> :
          <Notifications fontSize="medium" />
  }));

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Personalized Greeting */}
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Welcome back, {USER_NAME}!
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={2}>
        Here's an overview of the Tana Award platform performance and nominations
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} mb={4}>
        {metricsWithIcons.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Charts and Analytics Section */}
      <Grid container spacing={3} mb={4}>
        {/* Nominations Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Nomination Trends
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={nominationsChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Voting Activity Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Voting Activity
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={votingChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Distribution Charts and Activities */}
      <Grid container spacing={3}>
        {/* Award Categories Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Award Categories Distribution
              </Typography>
              <Box sx={{ height: 250 }}>
                <Doughnut 
                  data={awardCategoriesChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Nominee Status Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Nominee Status Overview
              </Typography>
              <Box sx={{ height: 250 }}>
                <Doughnut 
                  data={nomineeStatusChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Nominees */}
        <Grid item xs={12} md={4}>
          <TopNominees nominees={topNominees} />
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={8}>
          <RecentActivities activities={recentActivities} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;


